import {
  createFrontendModule,
  type ExtensionDefinition,
} from '@backstage/frontend-plugin-api';
import rbacPlugin from '@backstage-community/plugin-rbac';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import homePlugin from '@backstage/plugin-home/alpha';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import {
  Icon,
  iconBatch,
  iconHome,
  iconKey,
  iconNotification,
  iconPlus,
  iconReport,
  iconSearch,
  iconSettings,
} from '@ns/plugin-ns-theme';

interface PageIconOverride {
  page: ExtensionDefinition;
  pageId: string;
  pluginId: string;
}

/**
 * The upstream pages in the sidebar, each with its ns-theme glyph as page
 * icon. The toolbar shows it, and the sidebar reads the glyph out of it. Only
 * params change: the loader, inputs and config of each page stay the plugin's
 * own. A page passes the icon as a bare `<Icon glyph={...} />`, because the
 * sidebar only reads the glyph out of that form. Home also turns off its
 * upstream `noHeader`, so the homepage gets the same toolbar as the other
 * pages.
 */
export const pageIconOverrides: readonly PageIconOverride[] = [
  {
    page: homePlugin.getExtension('page:home').override({
      params: { icon: <Icon glyph={iconHome} />, noHeader: false },
    }),
    pageId: 'page:home',
    pluginId: homePlugin.pluginId,
  },
  {
    page: searchPlugin.getExtension('page:search').override({
      params: { icon: <Icon glyph={iconSearch} /> },
    }),
    pageId: 'page:search',
    pluginId: searchPlugin.pluginId,
  },
  {
    page: scaffolderPlugin.getExtension('page:scaffolder').override({
      params: { icon: <Icon glyph={iconPlus} /> },
    }),
    pageId: 'page:scaffolder',
    pluginId: scaffolderPlugin.pluginId,
  },
  {
    page: techdocsPlugin.getExtension('page:techdocs').override({
      params: { icon: <Icon glyph={iconReport} /> },
    }),
    pageId: 'page:techdocs',
    pluginId: techdocsPlugin.pluginId,
  },
  {
    page: catalogPlugin.getExtension('page:catalog').override({
      params: { icon: <Icon glyph={iconBatch} /> },
    }),
    pageId: 'page:catalog',
    pluginId: catalogPlugin.pluginId,
  },
  {
    page: rbacPlugin.getExtension('page:rbac').override({
      params: { icon: <Icon glyph={iconKey} /> },
    }),
    pageId: 'page:rbac',
    pluginId: rbacPlugin.pluginId,
  },
  {
    page: notificationsPlugin.getExtension('page:notifications').override({
      params: { icon: <Icon glyph={iconNotification} /> },
    }),
    pageId: 'page:notifications',
    pluginId: notificationsPlugin.pluginId,
  },
  {
    page: userSettingsPlugin.getExtension('page:user-settings').override({
      params: { icon: <Icon glyph={iconSettings} /> },
    }),
    pageId: 'page:user-settings',
    pluginId: userSettingsPlugin.pluginId,
  },
];

/**
 * One frontend module per upstream plugin, built from `pageIconOverrides`, so
 * the app registers exactly the list the test checks. A module replaces the
 * page extension by id, next to other modules of the same plugin (such as the
 * home dashboard), and drops out when its plugin is not loaded, so the
 * override never brings back a plugin that discovery leaves out.
 */
export const pageIconModules = pageIconOverrides.map(({ page, pluginId }) =>
  createFrontendModule({ extensions: [page], pluginId }),
);
